import React, { useState, useRef, useEffect } from "react";
import dayjs from "dayjs";
import TableActions, { ActionButton } from "../../../common/TableActions";
import {
  ACTIONS,
  ACTIVITY_STATUS,
  FILTERS,
  MONTHS_ENUM,
  STATUS_MAPPING,
} from "../../../common/Constants";
import { getAllVendorActivityIds, useGetAllVendorActivities } from "../../../../backend/query";
import {
  ACTIVITY_TYPE,
  ACTIVITY_TYPE_ICONS,
  API_DELIMITER,
  API_RESULT,
  ERROR_MESSAGES,
} from "../../../../utils/constants";
import Table, {
  CellTmpl,
  DEFAULT_PAYLOAD,
  TitleTmpl,
  reactFormatter,
} from "../../../common/Table";
import AdvanceSearch from "../../../common/AdvanceSearch";
import { preventDefault, getMaxMonthYear } from "../../../../utils/common";
import AlertModal from "../../../common/AlertModal";
import PageLoader from "../../../shared/PageLoader";
import Icon from "../../../common/Icon";
import AdminLocations from "./AdminVendorScheduleLocation";
import ConfirmModal from "../../../common/ConfirmModal";
import { useDeleteVendorAuditSchedule } from "../../../../backend/masters";
import { toast } from "react-toastify";
import { hasUserAccess } from "../../../../backend/auth";
import { USER_PRIVILEGES } from "../../UserManagement/Roles/RoleConfiguration";
import { GetVendorAuditScheduleBreadcrumb } from "./Companies.constants";
import MastersLayout from "../MastersLayout";
import { AxiosError } from "axios";
import DatePicker from "react-multi-date-picker";
import { Button } from "react-bootstrap";
import AuditReplicationDrawer from "./AuditReplicationDrawer";

const AUDIT_MIN_DATE = (() => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 10);
  date.setMonth(0);
  date.setDate(1);
  return date;
})();

const AUDIT_MAX_DATE = getMaxMonthYear();

const SortFields: any = {
  "act.name": "act_name",
  "rule.name": "rule_name",
  "activity.name": "activity_activity_name",
  "associate_company.name": "associate_company_name",
  "location.name": "location_name",
  "activity.activity_type": "activity_activity_type"
};

const INCLUDE_ASSOCIATION_COLUMNS = {
  act: { only: ['name'] },
  rule: { only: ['name'] },
  activity: { only: ['name', 'activity_type'] },
  associate_company: { only: ['name'] },
  location: { only: ['name'] }
};

const INCLUDES_ASSOCIATIONS = ['act', 'rule', 'activity', 'associate_company', 'location'];

const DELETABLE_STATUSES = [ACTIVITY_STATUS.PENDING, ACTIVITY_STATUS.OVERDUE];

function isDeletable(row: any) {
  return DELETABLE_STATUSES.includes((row || {}).status);
}

function VendorAuditScheduleDetails(this: any) {
  const [breadcrumb] = useState(
    GetVendorAuditScheduleBreadcrumb("Vendor Audit Schedule Details")
  );
  const [activity, setActivity] = useState<any>();
  const [action, setAction] = useState(ACTIONS.NONE);
  const [data, setData] = useState<any>();
  const [params, setParams] = useState<any>();
  const [locationFilters, setLocationFilter] = useState<any>();
  const lfRef: any = useRef();
  lfRef.current = locationFilters;
  const [advaceSearchFilters, setAdvanceSearchFilters] = useState<any>();
  const afRef: any = useRef();
  afRef.current = advaceSearchFilters;
  const [selectedMonthYear, setSelectedMonthYear] = useState<any>(null);
  const [monthYearFilter, setMonthYearFilter] = useState<any>({});
  const myfRef: any = useRef();
  myfRef.current = monthYearFilter;
  const [locationMeta, setLocationMeta] = useState<any>({});
  const [payload, setPayload] = useState<any>();
  const payloadRef: any = useRef();
  payloadRef.current = payload;
  const { activities, total, isFetching, refetch } = useGetAllVendorActivities(
    payload,
    Boolean(hasFilters(null, "company_id_eq"))
  );

  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const eligibleSelectedRows = (selectedRows || []).filter(isDeletable);
  const [alertMessage, setAlertMessage] = useState<any>(null);
  const [deleteVendorAuditRequests, setDeleteVendorAuditRequests] = useState<any[]>([]);
  const [deleteCount, setDeleteCount] = useState<number>(0);
  const [vendorAuditDeleting, setVendorAuditDeleting] = useState<boolean>(false);
  const [delectingAction, setDeletingAction] = useState(ACTIONS.NONE);
  const { deleteAuditSchedule, deleting } = useDeleteVendorAuditSchedule(
    (response: any) => {
      if (response instanceof AxiosError){
          const data = (response.response || {}).data;
          const error = (data || {}).error || ERROR_MESSAGES.DEFAULT;
          toast.error(error);
      } else {
          if(delectingAction == ACTIONS.BULK_DELETE){
              setDeleteCount(deleteCount + 1);
          } else if(delectingAction == ACTIONS.DELETE) {
              toast.success(`${selectedRows.length > 1 ? "Activities" : "Activity"} deleted successfully.`);
              setVendorAuditDeleting(false);
              setDeleteCount(0);
              setDeleteVendorAuditRequests([]);
              setDeletingAction(ACTIONS.NONE);
              dismissAction();
              refetch();
          }
      }
    }
  );

  useEffect(() => {
      if (delectingAction == ACTIONS.BULK_DELETE && deleteCount != eligibleSelectedRows.length) {
          const tempRequests = [...deleteVendorAuditRequests];
          const id = tempRequests.shift();
          setDeleteVendorAuditRequests(tempRequests);
          deleteAuditSchedule({id});
      } else if (delectingAction == ACTIONS.BULK_DELETE && deleteCount === eligibleSelectedRows.length) {
          toast.success(`${eligibleSelectedRows.length > 1 ? "Activities" : "Activity"} deleted successfully.`);
          setVendorAuditDeleting(false);
          setDeleteCount(0);
          setDeleteVendorAuditRequests([]);
          setDeletingAction(ACTIONS.NONE);
          dismissAction();
          refetch();
      }
  }, [deleteCount]);

  function hasFilters(ref: any, field = "company_id") {
    const tempPayloadRef = { ...(payloadRef.current || {}) };
    const _filters = (ref ? ref.current : tempPayloadRef.filters) || [];
    const company_id = _filters[field];
    return company_id || "";
  }

  function onLocationChange(event: any) {
    const {
      company,
      company_label,
      associate_company,
      associate_company_label,
      location_id,
      location_label,
      vendor_category_id,
      vendor_category_label,
      vendor_registration_id,
      vendor_registration_label,
    } = event;
    setLocationFilter({
        company_id_eq: company,
        associate_company_id_eq: associate_company,
        location_id_eq: location_id,
        vendor_category_id_eq: vendor_category_id || "",
        vendor_registration_id_eq: vendor_registration_id || "",
    });
    setLocationMeta({
      companyLabel: company_label || '',
      assocLabel: associate_company_label || '',
      locationLabel: location_label || '',
      vendorCategoryLabel: vendor_category_label || '',
      vendorLabel: vendor_registration_label || '',
    });
  }

  function onMonthYearChange(value: any) {
    setSelectedMonthYear(value);
    if (value) {
      const date = value.toDate ? value.toDate() : new Date(value);
      setMonthYearFilter({ month_eq: MONTHS_ENUM[date.getMonth()], year_eq: `${date.getFullYear()}` });
    } else {
      setMonthYearFilter({});
    }
  }

  function search(event: any) {
    const _filters: any = {};
    const keys = Object.keys(event || {});
    if (keys.length > 0) {
        keys.forEach(key => {
            _filters[key] = event[key]
        });
    }
    setAdvanceSearchFilters(_filters);
  }

  function dismissAction() {
    setAction(ACTIONS.NONE);
    setActivity(null);
  }

  function MonthTmpl({ cell }: any) {
    const row = cell.getData();
    return (
      <>
        {row.month} ({row.year})
      </>
    );
  }

  function DueDateTmpl({ cell }: any) {
    const value = cell.getValue();
    return (
      <span className="text-warn">{dayjs(value).format("DD-MM-YYYY")}</span>
    );
  }

  function FormStatusTmpl({ cell }: any) {
    const status = cell.getValue();
    return (
      <div className="d-flex align-items-center position-relative">
        <span className={`status-${status} ellipse`}>
          {STATUS_MAPPING[status] || status}
        </span>
      </div>
    );
  }

  function ActionColumnElements({ cell }: any) {
    const row = cell.getData();

    return (
      <div className="d-flex flex-row align-items-center position-relative">
        {hasUserAccess(USER_PRIVILEGES.DELETE_AUDIT_SCHEDULE_DETAILS) && isDeletable(row) && (
          <Icon
            className="mx-1"
            type="button"
            name="trash"
            text="Delete"
            data={row}
            action={handleDelete}
          />
        )}
      </div>
    );
  }

  function ActivityTypeTmpl({ cell }: any) {
    const value = cell.getValue() || ACTIVITY_TYPE.AUDIT;
    return (
      <div className="d-flex flex-row align-items-center justify-content-center position-relative">
        <Icon name={ACTIVITY_TYPE_ICONS[value]} text={value} />
      </div>
    );
  }

  const columns = [
    {
      title: "",
      field: "auditted",
      width: 40,
      formatter: reactFormatter(<ActivityTypeTmpl />),
    },
    {
      title: "Month & Year",
      field: "month",
      width: 140,
      formatter: reactFormatter(<MonthTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
    {
      title: "Act",
      field: "act.name",
      widthGrow: 2,
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
    {
      title: "Rule",
      field: "rule.name",
      widthGrow: 2,
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
    {
      title: "Activity",
      field: "activity.name",
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
      widthGrow: 2,
    },
    {
      title: "Activity Type",
      field: "activity.activity_type",
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
    {
      title: "Associate Company",
      field: "associate_company.name",
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
      widthGrow: 1,
    },
    {
      title: "Location Name",
      field: "location.name",
      formatter: reactFormatter(<CellTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
      widthGrow: 1,
    },
    {
      title: "Audit Due Date",
      field: "due_date",
      width: 120,
      formatter: reactFormatter(<DueDateTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
    {
      title: "Forms Status",
      field: "status",
      width: 160,
      formatter: reactFormatter(<FormStatusTmpl />),
      titleFormatter: reactFormatter(<TitleTmpl />),
    },
    {
      title: "Actions",
      hozAlign: "center",
      width: 120,
      headerSort: false,
      formatter: reactFormatter(<ActionColumnElements />),
      titleFormatter: reactFormatter(<TitleTmpl />),
      visible: hasUserAccess(USER_PRIVILEGES.DELETE_AUDIT_SCHEDULE_DETAILS),
    },
  ];

  const [tableConfig] = useState({
    paginationMode: "remote",
    ajaxRequestFunc,
    columns,
    rowHeight: "auto",
    selectable: true,
    paginate: true,
    initialSort: ['month desc'],
  });

  function formatApiResponse(params: any, list: any[], totalRecords: number) {
    const { page, per } = params || {};
    const tdata = {
      data: list,
      total: totalRecords,
      last_page: Math.ceil(totalRecords / (per || 1)) || 1,
      page: page || 1,
    };
    setData(tdata);
    return tdata;
  }

  function ajaxRequestFunc(url: any, config: any, params: any) {
    const { field, dir } = (params.sort || [])[0] || {};
    const _params = {
      page: params.page,
      per: params.size,
      sorts: [`${SortFields[field] || field || 'month'} ${dir || 'desc'}`],
      includes: INCLUDES_ASSOCIATIONS,
      include: INCLUDE_ASSOCIATION_COLUMNS
    };
    setParams(_params);
    setPayload({
      ...DEFAULT_PAYLOAD,
      ..._params,
      filters: {
        ...(lfRef.current || {}),
        ...(myfRef.current || {}),
        ...(afRef.current || {}),
      }
    });
    return Promise.resolve(formatApiResponse(params, activities, total));
  }

  function handlePageNav(_pagination: any) {
    const _params = { ..._pagination };
    setParams({ ..._params });
    setPayload({ ...payload, ..._params });
  }

  function getAdvanceSearchPayload() {
    const _filters = {...(afRef.current || {})};
    return _filters;
  }

  function handleDelete(_activity: any) {
    setActivity(_activity);
    setAction(ACTIONS.DELETE);
    setDeletingAction(ACTIONS.DELETE);
  }

  function performDelete() {
    setVendorAuditDeleting(true);
    deleteAuditSchedule({id: activity.id});
  }

  function performBulkDelete() {
    const requests = eligibleSelectedRows.map((x: any) => x.id);
    const id = requests.shift();
    setDeleteVendorAuditRequests(requests);
    setVendorAuditDeleting(true);
    deleteAuditSchedule({id});
  }

  function handleBulkDelete(e: any) {
    preventDefault(e);
    if ((selectedRows || []).length > 0) {
      setAction(ACTIONS.BULK_DELETE);
      setDeletingAction(ACTIONS.BULK_DELETE);
    }
  }

  const [replicationSource, setReplicationSource] = useState<any>(null);
  const [resolvingReplication, setResolvingReplication] = useState(false);

  async function handleReplicate(e: any) {
    preventDefault(e);
    let idsToReplicate = (selectedRows || []).map((r: any) => r.id);
    if (idsToReplicate.length === 0) {
      setResolvingReplication(true);
      const currentFilters = {
        ...(lfRef.current || {}),
        ...(myfRef.current || {}),
        ...(afRef.current || {})
      };
      idsToReplicate = await getAllVendorActivityIds(currentFilters);
      setResolvingReplication(false);
    }
    setReplicationSource({
      companyId: (locationFilters && locationFilters.company_id_eq) || '',
      companyLabel: locationMeta.companyLabel || '',
      associateCompanyId: (locationFilters && locationFilters.associate_company_id_eq) || '',
      associateCompanyLabel: locationMeta.assocLabel || '',
      locationId: (locationFilters && locationFilters.location_id_eq) || '',
      locationLabel: locationMeta.locationLabel || '',
      fromMonth: (monthYearFilter && monthYearFilter.month_eq) || '',
      fromYear: parseInt((monthYearFilter && monthYearFilter.year_eq) || '0'),
      vendorCategoryId: (locationFilters && locationFilters.vendor_category_id_eq) || '',
      vendorCategoryLabel: locationMeta.vendorCategoryLabel || '',
      vendorRegistrationId: (locationFilters && locationFilters.vendor_registration_id_eq) || '',
      vendorLabel: locationMeta.vendorLabel || '',
      selectedIds: idsToReplicate
    });
    setAction(ACTIONS.REPLICATE);
  }

  const hasReplicateFiltersSelected = !!(
    locationFilters &&
    locationFilters.company_id_eq &&
    locationFilters.associate_company_id_eq &&
    locationFilters.location_id_eq &&
    locationFilters.vendor_category_id_eq &&
    locationFilters.vendor_registration_id_eq &&
    monthYearFilter &&
    monthYearFilter.month_eq &&
    monthYearFilter.year_eq
  );
  const canReplicate = hasReplicateFiltersSelected && (total || 0) > 0;

  const actionButtons: ActionButton[] = [
    {
      name: 'replicate',
      label: resolvingReplication ? 'Please wait...' : 'Replicate',
      icon: 'copy',
      privilege: USER_PRIVILEGES.VIEW_AUDIT_VENDOR_SCHEDULE_DETAILS,
      disabled: !canReplicate || resolvingReplication,
      title: !hasReplicateFiltersSelected
        ? "Select Company, Associate Company, Location, Vendor Category, Vendor and Month & Year to enable Replicate"
        : (!canReplicate ? "No data available for replication" : undefined),
      action: handleReplicate
    },
    {
      name: 'bulkDelete',
      label: 'Bulk Delete',
      icon: 'trash',
      privilege: USER_PRIVILEGES.DELETE_AUDIT_SCHEDULE_DETAILS,
      disabled: !(selectedRows || []).length,
      title: !(selectedRows || []).length ? "Select the activities first" : undefined,
      action: handleBulkDelete
    }
  ];

  function onSelectionChange(_selectedRows: any) {
    setSelectedRows(_selectedRows);
  }

  useEffect(() => {
    if (locationFilters) {
      setPayload({
        ...payload,
        ...DEFAULT_PAYLOAD,
        sorts: ['month desc'],
        ...params,
        filters: {
          ...locationFilters,
          ...(myfRef.current || {}),
          ...(afRef.current || {}),
        }
      });
    }
  }, [locationFilters]);

  useEffect(() => {
    if (advaceSearchFilters) {
      setPayload({
        ...payload,
        ...DEFAULT_PAYLOAD,
        sorts: ['month desc'],
        ...params,
        filters: {
          ...(lfRef.current || {}),
          ...(myfRef.current || {}),
          ...advaceSearchFilters,
        }
      });
    }
  }, [advaceSearchFilters]);

  useEffect(() => {
    setPayload({
      ...payload,
      ...DEFAULT_PAYLOAD,
      sorts: ['month desc'],
      ...params,
      filters: {
        ...(lfRef.current || {}),
        ...monthYearFilter,
        ...(afRef.current || {}),
      }
    });
  }, [monthYearFilter]);

  useEffect(() => {
    if (!isFetching && payload) {
      setSelectedRows([]);
      setData(formatApiResponse(params, activities, total));
    }
  }, [isFetching]);

  return (
    <>
      <MastersLayout
        title="Vendor Audit Schedule Details"
        breadcrumbs={breadcrumb}
      >
        <form className="p-0 mx-3 my-2">
          <div className="card shadow border-0 p-2 mt-2 mb-3 filters">
            <div className="d-flex flex-row flex-wrap row-gap-2 m-0 align-items-end">
              <AdminLocations onChange={onLocationChange} />
              <div className="px-1 me-1">
                <label className="filter-label"><small>Month & Year</small></label>
                <div className="filter-month-picker month-picker">
                  <DatePicker
                    onlyMonthPicker={true}
                    editable={false}
                    onOpenPickNewDate={false}
                    minDate={AUDIT_MIN_DATE}
                    maxDate={AUDIT_MAX_DATE}
                    format={'MMM, YYYY'}
                    value={selectedMonthYear}
                    onChange={onMonthYearChange}
                    placeholder="Select Month & Year"
                  >
                    {selectedMonthYear && (
                      <Button variant="link" className="mb-2" onClick={() => onMonthYearChange(null)}>Clear</Button>
                    )}
                  </DatePicker>
                </div>
              </div>
              <div className="ms-auto d-flex flex-wrap gap-2 align-items-end justify-content-end">
                <div className="px-1">
                  <AdvanceSearch
                    fields={[FILTERS.SUBMITTED_DATE, FILTERS.ACTIVITY_TYPE]}
                    payload={getAdvanceSearchPayload()}
                    onSubmit={search}
                    todoType="location"
                  />
                </div>
                <TableActions buttons={actionButtons} />
              </div>
            </div>
          </div>
        </form>
        <Table
          data={data}
          options={tableConfig}
          isLoading={isFetching}
          onSelectionChange={onSelectionChange.bind(this)}
          onPageNav={handlePageNav}
        />
      </MastersLayout>
      {!!alertMessage && (
        <AlertModal
          message={alertMessage}
          onClose={(e: any) => {
            preventDefault(e);
            setAlertMessage(null);
          }}
        />
      )}
      {action === ACTIONS.DELETE && (
        <ConfirmModal
          title={"Delete Activity"}
          onSubmit={performDelete}
          onClose={() => setAction(ACTIONS.NONE)}
        >
          <div className="text-center mb-4">
            Are you sure you want to delete the activity ?
          </div>
        </ConfirmModal>
      )}
      {action === ACTIONS.BULK_DELETE && (
        <ConfirmModal
          title={"Delete Activities"}
          onSubmit={performBulkDelete}
          onClose={() => setAction(ACTIONS.NONE)}
          disableSubmit={eligibleSelectedRows.length === 0}
        >
          <p className="text-center mb-2">
            There are {selectedRows.length} record(s) selected for deleting.
          </p>
          <p className="text-center mb-4">
            Only <strong>Pending</strong> and <strong>Overdue</strong> activities can be deleted
            {eligibleSelectedRows.length === 0
              ? ' — none of the selected records qualify.'
              : ` (${eligibleSelectedRows.length} of ${selectedRows.length} selected record(s) qualify). Other selected records will be ignored.`}
          </p>
          <div className="text-center mb-4">
            Are you sure you want to delete the eligible activities ?
          </div>
        </ConfirmModal>
      )}
      {vendorAuditDeleting && <PageLoader message={delectingAction === ACTIONS.BULK_DELETE ? `Deleting ${deleteCount + 1} of ${eligibleSelectedRows.length}` : 'Deleting...'} />}
      {action === ACTIONS.REPLICATE && replicationSource && (
        <AuditReplicationDrawer
          companyId={replicationSource.companyId}
          companyLabel={replicationSource.companyLabel}
          associateCompanyId={replicationSource.associateCompanyId}
          associateCompanyLabel={replicationSource.associateCompanyLabel}
          locationId={replicationSource.locationId}
          locationLabel={replicationSource.locationLabel}
          fromMonth={replicationSource.fromMonth}
          fromYear={replicationSource.fromYear}
          selectedIds={replicationSource.selectedIds || []}
          selectedCount={(replicationSource.selectedIds || []).length || total}
          totalCount={total}
          onClose={() => { setAction(ACTIONS.NONE); setReplicationSource(null); }}
          onSuccess={() => { setAction(ACTIONS.NONE); setReplicationSource(null); refetch(); }}
          isVendor={true}
          vendorCategoryId={replicationSource.vendorCategoryId}
          vendorCategoryLabel={replicationSource.vendorCategoryLabel}
          vendorRegistrationId={replicationSource.vendorRegistrationId}
          vendorLabel={replicationSource.vendorLabel}
        />
      )}
    </>
  );
}

export default VendorAuditScheduleDetails;
